<template lang="pug">
.insights-ranking-widget.insights-widget
  InsightsRankingDialog(
    v-if="showRankingDialog"
    :show="showDialog"
    :type="type"
    :widget-key="widgetKey"
    :title="title"
    @close="toggleDialog"
  )
  .widget-wrapper
    .widget-common.widget-full-height(:class="sizeClass")
      .widget-title.d-flex.align-items-center.justify-space-between
        div(:class="titleClasses")
          | {{ label }}
          i.hi.hi-info-filled.ml-5(
            v-if="tooltip"
            v-tooltip="tooltipOptions"
            :title="tooltip"
          )
          img.suspicious-icon(
            v-if="showSuspiciousIcon && !isLoading"
            v-tooltip="tooltipOptions"
            :src="suspiciousIcon"
            alt="suspicious info"
            :title="suspiciousTooltip"
          )
        a.insights-widget-view-more(
          v-if="showDialogButton"
          v-track:click="trackToggleProperties"
          @click.prevent="toggleDialog"
        )
          i.hi.hi-external
          span View more

      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          .data-privacy(v-if="hasDataPrivacy")
            img.w-full(
              :class="`blurred-items-${limit}`"
              :src="dataPrivacyPlaceholder"
            )
          InsightsRankingItems(
            v-else
            :items="items"
            :type="type"
            :widget-data="widgetData"
            :widget-key="widgetKey"
            :widget-name="widgetName"
            :label="label"
          )
</template>

<script>
import { Insights, InsightsTracking } from '~/mixins';
import { ICON_SHIELD, ICON_SHIELD_WARNING } from '~/rails_assets';
import { INSIGHTS, INSIGHTS_ASSETS as insightsAssets } from '~/app_constants';
import { mapGetters, mapActions, mapState } from 'vuex';
import InsightsRankingDialog from './InsightsRankingDialog.vue';
import InsightsRankingItems from './InsightsRankingItems.vue';

export default {
  name: 'InsightsRankingWidget',
  components: {
    InsightsRankingDialog,
    InsightsRankingItems,
  },
  mixins: [Insights, InsightsTracking],
  props: {
    label: {
      type: String,
      required: true,
    },
    widgetKey: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      default: 'medium',
    },
    limit: {
      type: Number,
      default: 10,
    },
    prefix: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    fetchData: {
      type: Boolean,
      default: true,
    },
    viewMore: {
      type: Boolean,
      default: false,
    },
    viewMoreLabel: {
      type: String,
      default: '',
    },
    hasSuspiciousFeature: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
    showDialog: false,
  }),
  computed: {
    ...mapState(['widgets', 'loadingWidgets']),
    ...mapGetters(['canSeeTeam']),
    widgetData() {
      return this.widgets[this.widgetName] || {};
    },
    isLoading() {
      return this.loadingWidgets[this.widgetName];
    },
    hasData() {
      if (!this.widgets[this.widgetName]) {
        return false;
      }
      const data = this.widgets[this.widgetName].data || this.widgets[this.widgetName];

      return !!(Array.isArray(data) && data.length);
    },
    hasDataOrLoading() {
      return this.hasData || this.isLoading;
    },
    items() {
      if (!this.widgets[this.widgetName]) {
        return;
      }

      // for the tools_ranking widget we now have different data structure
      // [] => { data: [], has_suspicious_activity: true }
      // later in another task's scope we'll put the data management outside this component.
      // For now, we need to handle this here using simple condition
      const widgetData = this.widgets[this.widgetName].data
        ? this.widgets[this.widgetName].data
        : this.widgets[this.widgetName];

      if (!this.fetchData) {
        return widgetData.slice(0, this.limit);
      }

      return widgetData;
    },
    sizeClass() {
      return `widget-${this.size}`;
    },
    widgetName() {
      return this.prefix ? `${this.prefix}_${this.widgetKey}` : this.widgetKey;
    },
    hasItems() {
      return !!(this.items && this.items.length);
    },
    title() {
      return this.viewMoreLabel || this.label;
    },
    hasSuspiciousActivity() {
      return this.widgets[this.widgetName] && this.widgets[this.widgetName].suspicious_activity_detected;
    },
    suspiciousIcon() {
      return this.hasSuspiciousActivity ? ICON_SHIELD_WARNING : ICON_SHIELD;
    },
    suspiciousTooltip() {
      return this.hasSuspiciousActivity
        ? INSIGHTS.tooltips.rankings.suspiciousBehaviour.detected
        : INSIGHTS.tooltips.rankings.suspiciousBehaviour.undetected;
    },
    hasDataPrivacy() {
      return this.widgets[this.widgetName] && this.widgets[this.widgetName].data_hidden;
    },
    dataPrivacyPlaceholder() {
      return insightsAssets.ranking_placeholder[this.limit].src;
    },
    titleClasses() {
      return this.showSuspiciousIcon ? 'd-flex align-items-center' : 'd-flex align-items-baseline';
    },
    showSuspiciousIcon() {
      return (
        this.hasSuspiciousFeature &&
        this.canSeeTeam &&
        this.widgets[this.widgetName] &&
        this.widgets[this.widgetName].data.length &&
        'suspicious_activity_detected' in this.widgets[this.widgetName]
      );
    },
    showDialogButton() {
      return this.viewMore && this.hasItems && !this.hasDataPrivacy;
    },
    showRankingDialog() {
      return this.viewMore && this.showDialog && this.hasItems;
    },
    trackToggleProperties() {
      return Object.freeze({
        label: 'Widget click',
        widget_name: this.label,
        action_type: 'details',
        action_label: 'View more',
        ...this.commonProps(),
      });
    },
  },
  created() {
    if (this.fetchData && !this.hasDataOrLoading) {
      this.fetchWidgets({
        widgetKey: this.widgetKey,
        widgetName: this.widgetName,
        extraQueryParams: {
          limit: this.limit,
        },
      });
    }
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    toggleDialog() {
      this.showDialog = !this.showDialog;
    },
  },
};
</script>
